import React from 'react';
import { Col } from 'react-bootstrap';
import { formatAmountWithLocale } from '../helper/common.helper';

interface TotalsDisplaySectionProps {
  isCalculateInvoiceLoading: boolean;
  calculatedSubAmount: any;
  currency: string;
  language: string;
  vat: number;
  vatAmount: number;
  isCalculationLoading: boolean;
  handleShowCalculationBreakdown: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  t: (key: string) => string;
}

export const TotalsDisplaySection: React.FC<TotalsDisplaySectionProps> = ({
  isCalculateInvoiceLoading,
  calculatedSubAmount,
  currency,
  language,
  vat,
  vatAmount,
  isCalculationLoading,
  handleShowCalculationBreakdown,
  t,
}) => {
  // Payout can't cover fees/tax: negative, or clamped to 0 on a non-zero invoice.
  const totalCalc = calculatedSubAmount?.response?.totalCalculation;
  const net: number = totalCalc?.netInSek || totalCalc?.net || 0;
  const isPayoutNegative =
    !!calculatedSubAmount?.response?.isPayoutNegative ||
    net < 0 ||
    ((totalCalc?.amountIncludingVat ?? 0) > 0 && net <= 0);

  return (
    <Col xs={12} md={12} lg={12} xl={5} xxl={4} className="totals-section">
      <div className="totals-box">
        <h3 className="totals-title">{t('WIZARD_STEP_SUMMARY_TITLE')}</h3>
        <div className="subtotal-row">
          <span className="total-label">{t('SUB_TOTAL')} :</span>
          <div className="total-values">
            <div className="total-amount">
              {isCalculateInvoiceLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                formatAmountWithLocale(
                  calculatedSubAmount?.response?.totalCalculation.amount || 0,
                  currency,
                  language
                )
              )}
            </div>
            <div className="vat-text">
              +{vat}% {t('VAT_LABEL')} ({(() => {
                const vatDisplayAmount = calculatedSubAmount?.response?.totalCalculation?.vat || 0;
                return vatDisplayAmount > 0 ? formatAmountWithLocale(vatDisplayAmount, currency, language) : '0';
              })()})
            </div>
          </div>
        </div>
       {(() => {
          const calc = calculatedSubAmount?.response?.totalCalculation;
          if (!calc) return null;
          const rutAmt = calc.rutDeductionAmount || 0;
          const rotAmt = calc.rootDeductionAmount || 0;
          if (rutAmt <= 0 && rotAmt <= 0) return null;
          const customerPays = calc.amountIncludingVatAfterHouseholdServiceDeduction || 0;
          return (
            <div className="totals-deduction-breakdown">
              {rutAmt > 0 && (
                <div className="totals-deduction-row">
                  <div className="totals-deduction-left">
                    <span className="totals-deduction-badge totals-deduction-badge--rut">RUT</span>
                    <span className="totals-deduction-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                  </div>
                  <span className="totals-deduction-value">-{formatAmountWithLocale(rutAmt, currency, language)}</span>
                </div>
              )}
              {rotAmt > 0 && (
                <div className="totals-deduction-row">
                  <div className="totals-deduction-left">
                    <span className="totals-deduction-badge totals-deduction-badge--rot">ROT</span>
                    <span className="totals-deduction-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                  </div>
                  <span className="totals-deduction-value">-{formatAmountWithLocale(rotAmt, currency, language)}</span>
                </div>
              )}
              <div className="totals-deduction-row totals-deduction-row--customer-pay">
                <span className="totals-deduction-label totals-deduction-label--bold">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                <span className="totals-deduction-value totals-deduction-value--primary">{formatAmountWithLocale(customerPays, currency, language)}</span>
              </div>
            </div>
          );
        })()}
       <div className="final-total-row">
          <span className="total-label">{t('TOTAL')} :</span>
          <span className="final-amount">
            {isCalculateInvoiceLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              formatAmountWithLocale(
                 calculatedSubAmount?.response?.totalCalculation.amountIncludingVat || 0,
                currency,
                language
              )
            )}
          </span>
        </div>

        {/* Payout would be negative: the amount can't cover the Invozio fee, social fees and tax */}
        {!isCalculateInvoiceLoading && isPayoutNegative && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 12,
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#b91c1c',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            {t("PAYOUT_NEGATIVE_ERROR")}
          </div>
        )}

        {/* Total net salary paid out ("Amount paid to your account") */}
        <div className={`net-salary-row${isPayoutNegative ? ' payout-negative' : ''}`}>
          <span className="net-salary-label">{t('AMOUNT_PAID_TO_YOUR_ACCOUNT') || 'Amount paid to your account'} :</span>
          <span
            className="net-salary-value"
            style={isPayoutNegative ? { color: '#dc2626' } : undefined}
          >
            {isCalculateInvoiceLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              // Salary is always paid out in SEK, regardless of the invoice currency —
              // show the SEK net amount (netInSek) with a fixed SEK label.
              (() => {
                const net = calculatedSubAmount?.response?.totalCalculation?.netInSek || calculatedSubAmount?.response?.totalCalculation?.net || 0;
                // formatAmountWithLocale drops the sign, so re-add it for a negative payout.
                return `${net < 0 ? '-' : ''}${formatAmountWithLocale(net, 'SEK', language)}`;
              })()
            )}
          </span>
        </div>
        <div className="calculation-link">
          <a 
            href="#" 
            className={`link-text ${isCalculateInvoiceLoading ? 'disabled' : ''}`}
            onClick={isCalculateInvoiceLoading ? (e) => e.preventDefault() : handleShowCalculationBreakdown}
            style={{ 
              pointerEvents: isCalculateInvoiceLoading ? 'none' : 'auto',
              opacity: isCalculateInvoiceLoading ? 0.5 : 1,
              cursor: isCalculateInvoiceLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isCalculationLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('LOADING') || 'Loading...'}
              </>
            ) : (
              <>
                {t('SEE_HOW_WE_CALCULATED')}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </a>
        </div>
      </div>
    </Col>
  );
};
